import { ReactNode, createElement, useCallback } from 'react';

import { CssVarsProvider } from '@mui/joy/styles';
import { flushSync } from 'react-dom';
import { Root, createRoot } from 'react-dom/client';

import { HomeOnboardingPopover } from '../../components/onboarding/popover/HomeOnboardingPopover';
import { theme } from '../../theme';
import { brand } from '../../theme/brand';
import { track } from '../analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_closed,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../analytics/events';
import { driver } from '../driverjs';

export type OnboardingStep = {
  element: string;
  side: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  title: string;
  body: string;
  show: boolean;
  customContent?: ReactNode;
  interactive?: boolean;
};

export const hasVisibleOnboardingSteps = (steps: OnboardingStep[]): boolean =>
  steps.some((step) => step.show);

// Tutorial analytics (analytics_sheet.pdf). The home tour shows on the
// dashboard, and per the sheet feature_name is the tutorial-level subject
// ("Dashboard"), constant across screens. tutorial_step_number tells screens
// apart by position, and tutorial_screen_header_text = "the first few words of
// the header copy" (the stable per-screen identifier, since step numbers can
// vary per user).
const TUTORIAL_PLATFORM_PAGE = 'dashboard';
const TUTORIAL_FEATURE_NAME = 'dashboard';
const tourHeaderText = (title?: string): string => (title ?? '').split(' ').slice(0, 3).join(' ');

const TOUR_THEME_CSS_ID = 'driverjs-onboarding-theme-style';
const injectTourThemeStyle = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TOUR_THEME_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = TOUR_THEME_CSS_ID;
  style.textContent = `
    .driver-fade .driver-popover.driverjs-onboarding-theme { animation: none !important; }
    /* driver.js's auto-positioned arrow is unreliable when floating-ui shifts
       the popover; we draw our own triangle (.calyx-tour-arrow) anchored to
       the popover edge facing the highlight. */
    .driverjs-onboarding-theme .driver-popover-arrow { display: none !important; }
    .calyx-tour-arrow {
      position: fixed;
      width: 0;
      height: 0;
      border: 10px solid transparent;
      z-index: 100000;
      pointer-events: none;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
    }
    .calyx-tour-arrow[data-side="left"]   {
      border-right-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(-100%, -50%);
    }
    .calyx-tour-arrow[data-side="right"]  {
      border-left-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(0%, -50%);
    }
    .calyx-tour-arrow[data-side="top"]    {
      border-bottom-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(-50%, -100%);
    }
    .calyx-tour-arrow[data-side="bottom"] {
      border-top-color: var(--joy-palette-background-surface, #ffffff);
      transform: translate(-50%, 0%);
    }
    .calyx-tour-dot {
      position: fixed;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #127C16;
      pointer-events: none;
      animation: calyxTourPulse 1.4s ease-out infinite;
      z-index: 100001;
      transform: translate(-50%, -50%);
    }
    @keyframes calyxTourPulse {
      0%   { box-shadow: 0 0 0 0    rgba(18, 124, 22, 0.5); }
      70%  { box-shadow: 0 0 0 14px rgba(18, 124, 22, 0);   }
      100% { box-shadow: 0 0 0 0    rgba(18, 124, 22, 0);   }
    }
  `;
  document.head.appendChild(style);
};

// Extra analytics props merged into every tutorial event on the tour. The
// dashboard tour highlights "Use a Fastpass", so QA wants the org's fastpass
// counts (fastpass_used / fastpass_total) on each screen's events. Captured at
// tour-start by the launcher (a React component) and threaded in here, since
// runOnboardingTour is a plain imperative function and can't read context.
export type TourEventProps = Record<string, string | number | null>;

export const runOnboardingTour = (steps: OnboardingStep[], eventProps: TourEventProps = {}) => {
  const visibleSteps = steps.filter((step) => step.show);
  if (visibleSteps.length === 0) return;

  injectTourThemeStyle();

  const isDarkMode = document.documentElement.getAttribute('data-joy-color-scheme') === 'dark';
  const overlayColor = isDarkMode ? brand.blur.light : brand.blur.dark;
  const overlayOpacity = isDarkMode ? 0.2 : 0.1;

  let popoverRoot: Root | null = null;
  let customContainer: HTMLDivElement | null = null;
  let interactiveTarget: HTMLElement | null = null;
  let interactiveHandler: ((event: Event) => void) | null = null;

  let lastShownIdx = -1;
  const stepEventProps = (idx: number) => {
    const step = visibleSteps[idx];
    return {
      platform_page: TUTORIAL_PLATFORM_PAGE,
      feature_name: TUTORIAL_FEATURE_NAME,
      tutorial_step_number: `Step ${idx + 1}`,
      tutorial_screen_header_text: tourHeaderText(step?.title),
      // fastpass_used / fastpass_total (and any future per-tour context) on
      // every tutorial event — PGV, main CTA, secondary CTA, close.
      ...eventProps,
    };
  };

  const detachInteractiveListener = () => {
    if (interactiveTarget && interactiveHandler) {
      interactiveTarget.removeEventListener('click', interactiveHandler);
    }
    interactiveTarget = null;
    interactiveHandler = null;
  };

  const attachInteractiveListener = (idx: number) => {
    detachInteractiveListener();
    const step = visibleSteps[idx];
    if (!step?.interactive) return;
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return;
    const handler = () => {
      if (idx === visibleSteps.length - 1) tour.destroy();
      else tour.moveNext();
    };
    target.addEventListener('click', handler);
    interactiveTarget = target;
    interactiveHandler = handler;
  };

  // Standalone green dot pinned to the highlight's corner facing the popover —
  // independent of driver.js's arrow (which we hide), so it's never thrown off
  // by floating-ui's overflow shifting of the popover.
  let floatingDot: HTMLDivElement | null = null;
  let floatingArrow: HTMLDivElement | null = null;
  const ensureDot = (): HTMLDivElement => {
    if (!floatingDot) {
      floatingDot = document.createElement('div');
      floatingDot.className = 'calyx-tour-dot';
      document.body.appendChild(floatingDot);
    }
    return floatingDot;
  };
  const ensureArrow = (): HTMLDivElement => {
    if (!floatingArrow) {
      floatingArrow = document.createElement('div');
      floatingArrow.className = 'calyx-tour-arrow';
      document.body.appendChild(floatingArrow);
    }
    return floatingArrow;
  };
  const removeOverlayBits = () => {
    if (floatingDot && floatingDot.parentNode) floatingDot.parentNode.removeChild(floatingDot);
    if (floatingArrow && floatingArrow.parentNode)
      floatingArrow.parentNode.removeChild(floatingArrow);
    floatingDot = null;
    floatingArrow = null;
  };
  const rectsOverlap = (a: DOMRect, b: DOMRect): boolean =>
    !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

  const positionDotForStep = (idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return removeOverlayBits();
    const target = document.querySelector(step.element);
    if (!(target instanceof HTMLElement)) return removeOverlayBits();
    const t = target.getBoundingClientRect();
    const popover = document.querySelector<HTMLElement>('.driver-popover');
    const p = popover?.getBoundingClientRect();
    // If after driver.js's own scrollIntoView the popover still overlaps the
    // highlight (target is taller than the viewport, for example) drop the
    // visual cues — popover-on-highlight is its own indicator.
    if (p && rectsOverlap(p, t)) return removeOverlayBits();
    const dot = ensureDot();
    const arrow = ensureArrow();
    // Decide which axis the popover sits on relative to the highlight, then
    // place the dot on the highlight's facing edge and the arrow on the
    // popover's facing edge — both aligned along the same axis so they read
    // as a single visual connection.
    let dotX = t.left + t.width / 2;
    let dotY = t.top + t.height / 2;
    let arrowX = dotX;
    let arrowY = dotY;
    let arrowSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
    if (p) {
      const targetCx = t.left + t.width / 2;
      const targetCy = t.top + t.height / 2;
      const popoverCx = p.left + p.width / 2;
      const popoverCy = p.top + p.height / 2;
      const dx = popoverCx - targetCx;
      const dy = popoverCy - targetCy;
      if (Math.abs(dx) > Math.abs(dy)) {
        // Popover sits to the side of the highlight (left or right).
        const popoverIsRight = dx > 0;
        dotX = popoverIsRight ? t.right : t.left;
        dotY = Math.max(t.top + 12, Math.min(t.bottom - 12, popoverCy));
        arrowX = popoverIsRight ? p.left : p.right;
        arrowY = dotY;
        arrowSide = popoverIsRight ? 'left' : 'right';
      } else {
        // Popover sits above or below the highlight.
        const popoverIsBelow = dy > 0;
        dotY = popoverIsBelow ? t.bottom : t.top;
        dotX = Math.max(t.left + 12, Math.min(t.right - 12, popoverCx));
        arrowY = popoverIsBelow ? p.top : p.bottom;
        arrowX = dotX;
        arrowSide = popoverIsBelow ? 'top' : 'bottom';
      }
    }
    dot.style.left = `${dotX}px`;
    dot.style.top = `${dotY}px`;
    arrow.style.left = `${arrowX}px`;
    arrow.style.top = `${arrowY}px`;
    arrow.dataset.side = arrowSide;
  };
  let lastIdx = 0;
  const repositionDot = () => positionDotForStep(lastIdx);
  window.addEventListener('resize', repositionDot);
  window.addEventListener('scroll', repositionDot, true);

  const mountReact = (popover: { wrapper: HTMLElement }, idx: number) => {
    const step = visibleSteps[idx];
    if (!step) return;

    const wrapper = popover.wrapper;
    wrapper.style.padding = '0';
    wrapper.style.maxWidth = 'none';
    wrapper.style.width = 'auto';
    wrapper.style.background = 'transparent';
    wrapper.style.boxShadow = 'none';
    wrapper.style.borderRadius = '10px';
    // Hide driver.js's default popover chrome but keep the arrow — the green
    // dot is positioned independently (anchored to the highlight) so it stays
    // accurate even when floating-ui shifts the popover to fit the viewport.
    Array.from(wrapper.children).forEach((child) => {
      if (child === customContainer) return;
      const el = child as HTMLElement;
      if (el.classList.contains('driver-popover-arrow')) return;
      el.style.display = 'none';
    });

    if (customContainer && customContainer.parentNode !== wrapper) {
      wrapper.appendChild(customContainer);
    }
    if (!customContainer) {
      customContainer = document.createElement('div');
      wrapper.appendChild(customContainer);
      popoverRoot = createRoot(customContainer);
    }

    flushSync(() => {
      popoverRoot?.render(
        createElement(
          CssVarsProvider,
          { theme, defaultMode: 'light' },
          createElement(HomeOnboardingPopover, {
            title: step.title,
            body: step.body,
            customContent: step.customContent,
            stepIndex: idx,
            totalSteps: visibleSteps.length,
            interactive: step.interactive,
            onBack: () => {
              track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text: 'Back',
              });
              if (idx > 0) tour.movePrevious();
            },
            onContinue: () => {
              track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
                ...stepEventProps(idx),
                button_text: idx === visibleSteps.length - 1 ? 'Finish' : 'Continue',
              });
              if (idx === visibleSteps.length - 1) tour.destroy();
              else tour.moveNext();
            },
            onClose: () => {
              track(EVNT_tutorial_screen_closed, EVENT_TUTORIAL, stepEventProps(idx));
              tour.destroy();
            },
          })
        )
      );
    });
  };

  const hasInteractiveStep = visibleSteps.some((step) => step.interactive);

  const tour = driver({
    showProgress: false,
    showButtons: [],
    allowClose: !hasInteractiveStep,
    smoothScroll: false,
    overlayColor,
    overlayOpacity,
    popoverClass: 'driverjs-onboarding-theme',
    onDestroyed: () => {
      detachInteractiveListener();
      removeOverlayBits();
      window.removeEventListener('resize', repositionDot);
      window.removeEventListener('scroll', repositionDot, true);
      const root = popoverRoot;
      popoverRoot = null;
      customContainer = null;
      if (root) queueMicrotask(() => root.unmount());
    },
    onPopoverRender: (popover, { state }) => {
      const idx = state.activeIndex ?? 0;
      // PGV once per step (onPopoverRender may fire on re-render).
      if (idx !== lastShownIdx) {
        lastShownIdx = idx;
        track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, stepEventProps(idx));
      }
      mountReact(popover, idx);
      attachInteractiveListener(idx);
      lastIdx = idx;
      // Position the floating dot on the next frame so the popover has settled
      // its final rect (we read it to decide which edge of the highlight to
      // anchor the dot to).
      requestAnimationFrame(() => positionDotForStep(idx));
    },
    steps: visibleSteps.map((s) => ({
      element: s.element,
      popover: { side: s.side, align: s.align ?? 'center', title: '', description: '' },
    })),
  });

  tour.drive();
};

export const useOnboardingTour = (steps: OnboardingStep[], eventProps: TourEventProps = {}) => {
  const canShow = hasVisibleOnboardingSteps(steps);
  const start = useCallback(() => runOnboardingTour(steps, eventProps), [steps, eventProps]);
  return { canShow, start };
};
